import {
  Box,
  Typography,
  Stack,
  debounce,
  StackProps,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';
import { buildThreeDBarChartOptions } from '../chartWidgetUtility';

export interface ThreeDBarChartData {
  name: string;
  value: number;
}

interface ThreeDBarChartProps {
  title: any;
  subTitle?: string;
  isLoading?: boolean;
  data: ThreeDBarChartData[];
  barColors?: string[];
  containerProps?: StackProps;
  tooltipLabel?: any;
  hasTooltip?: boolean;
  showGridLines?: boolean;
  showLegend?: boolean;
  showDataLabels?: boolean;
  tooltipFormatter?: (params: any) => string;
  valueType?: 'cost' | 'storage' | 'number';
  storageUnit?: 'KB' | 'MB' | 'GB' | 'TB' | 'PB';
  onBarClick?: (params: any) => void;
  barWidth?: number;
  chartHeight?: number;
  yAxisName?: string;
  yAxisMax?: number;
  yAxisInterval?: number;
  decimalPlaces?: number;
}

const ThreeDBarChart = ({
  title,
  subTitle,
  isLoading,
  data,
  barColors = [],
  containerProps,
  tooltipLabel,
  hasTooltip = true,
  showGridLines = true,
  showLegend = true,
  showDataLabels = true,
  tooltipFormatter,
  valueType = 'number',
  storageUnit = 'GB',
  onBarClick,
  barWidth = 100,
  chartHeight = 350,
  yAxisName,
  yAxisMax,
  yAxisInterval = 1,
  decimalPlaces = 2,
}: ThreeDBarChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const options = useMemo(
    () =>
      buildThreeDBarChartOptions({
        data,
        barColors,
        colors,
        showGridLines,
        showLegend,
        showDataLabels,
        tooltipFormatter,
        valueType,
        storageUnit,
        barWidth,
        yAxisName,
        yAxisMax,
        yAxisInterval,
        decimalPlaces,
      }),
    [
      data,
      barColors,
      colors,
      showGridLines,
      showLegend,
      showDataLabels,
      tooltipFormatter,
      valueType,
      storageUnit,
      barWidth,
      yAxisName,
      yAxisMax,
      yAxisInterval,
      decimalPlaces,
    ],
  );

  useEffect(() => {
    const onResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 200);

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      onResize.clear();
    };
  }, []);

  const renderChartContent = () => {
    if (isLoading) {
      return <LoaderComponent pt={20} />;
    }

    if (!options) {
      return (
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant="bodyText"
            sx={{ color: colors.widget.text.main }}
          >
            No Data Available
          </Typography>
        </Stack>
      );
    }

    return (
      <ReactECharts
        option={options}
        notMerge
        lazyUpdate
        style={{
          width: '100%',
          height: '100%',
        }}
        onEvents={{
          click: (params: any) => onBarClick?.(params),
        }}
      />
    );
  };

  return (
    <WidgetWraper>
      <Box
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
        }}
        {...containerProps}
      >
        <Stack gap={2}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="widgetTitleNew"
              sx={{ color: colors.widget.text.title }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && tooltipLabel && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {subTitle && (
            <Typography
              variant="bodyText"
              sx={{ color: colors.widget.text.title }}
            >
              {subTitle}
            </Typography>
          )}
        </Stack>

        <Box
          mt={2}
          height={chartHeight}
          key={windowSize}
        >
          {renderChartContent()}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default ThreeDBarChart;